import { forwardRef, type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { Box, InputAdornment, Stack, type StackProps, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { type Control, type FieldPath, type UseFormGetValues, useWatch, type UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InvoiceItemsFormPart } from '../../../invoice-app/components/ConnectedInvoiceItemsFormPart';
import { AresLookupButton, AresLookupResult } from '../../components/AresLookupButton';
import { FormDatePicker } from '../../components/form/FormDatePicker';
import { FormInput, FormInputProps } from '../../components/form/FormInput';
import { FormNumericInput, FormNumericInputProps } from '../../components/form/FormNumericInput';
import { FormSelect, FormSelectProps } from '../../components/form/FormSelect';
import { currencies } from '../../utils/currencies';
import { ConnectedFormFieldGroup } from '../forms/invoice/connected/ConnectedFormFieldGroup';
import { inputSharedProps } from '../forms/invoice/shared';
import type { InvoiceManagementInvoiceFormType } from '../forms/invoice/types';
import { InvoiceManagementCustomerFormPart } from './InvoiceManagementCustomerFormPart';
import { InvoiceManagementVendorFormPart } from './InvoiceManagementVendorFormPart';

type InvoiceManagementInvoiceFormProps = {
  control: Control<InvoiceManagementInvoiceFormType>;
  setValue: UseFormSetValue<InvoiceManagementInvoiceFormType>;
  getValues: UseFormGetValues<InvoiceManagementInvoiceFormType>;
  slots?: {
    customerSlot?: ReactNode;
    vendorSlot?: ReactNode;
    invoiceHeaderSlot?: ReactNode;
    vendorHeaderSlot?: ReactNode;
    customerHeaderSlot?: ReactNode;
  };
  readonly?: boolean;
  selectedCurrency?: string | null;
  formId?: string;
} & StackProps<'form'>;

export const InvoiceManagementInvoiceForm = forwardRef<HTMLFormElement, InvoiceManagementInvoiceFormProps>(
  (props, ref) => {
    const {
      control,
      slots,
      readonly,
      selectedCurrency,
      formId = 'invoice-form',
      setValue,
      getValues,
      ...stackProps
    } = props;

    const { t } = useTranslation();

    const taxPayer = useWatch({
      control,
      name: 'taxPayer',
    });

    //clear taxPayer related fields if taxPayer is false
    useEffect(() => {
      if (!readonly && taxPayer === false) {
        setValue('amountWithoutVat', null, { shouldValidate: true });
        setValue('amountVat', null, { shouldValidate: true });
        setValue('taxPoint', null, { shouldValidate: true });
        setValue('vendor.taxId', null, { shouldValidate: true });
      }
    }, [taxPayer, setValue, readonly]);

    const formInputSharedProps = useMemo(
      () =>
        ({
          ...inputSharedProps,
        }) satisfies Partial<FormSelectProps & FormInputProps>,
      [],
    );

    const handleAresLookupSuccess = useCallback(
      (namePrefix: 'customer' | 'vendor') => (result: AresLookupResult) => {
        if (result.taxId) {
          setValue(`${namePrefix}.taxId`, result.taxId, { shouldValidate: true });
          if (namePrefix === 'vendor') {
            setValue('taxPayer', true, { shouldValidate: true });
          }
        }
        if (result.name) {
          setValue(`${namePrefix}.name`, result.name, { shouldValidate: true });
        }
        if (result.address) {
          setValue(`${namePrefix}.address`, result.address, { shouldValidate: true });
        }
      },
      [setValue],
    );

    const CustomerSlot = useMemo(
      () =>
        slots?.customerSlot ?? (
          <InvoiceManagementCustomerFormPart
            inputProps={formInputSharedProps}
            readonly={readonly}
            control={control}
            slots={{
              actionArea: readonly ? null : (
                <AresLookupButton
                  getVatId={() => getValues('customer.vatId') ?? ''}
                  onLookupSuccess={handleAresLookupSuccess('customer')}
                />
              ),
            }}
          />
        ),
      [control, formInputSharedProps, readonly, slots?.customerSlot, getValues, handleAresLookupSuccess],
    );
    const VendorSlot = useMemo(
      () =>
        slots?.vendorSlot ?? (
          <InvoiceManagementVendorFormPart
            inputProps={formInputSharedProps}
            readonly={readonly}
            control={control}
            slot={
              readonly ? null : (
                <AresLookupButton
                  getVatId={() => getValues('vendor.vatId') ?? ''}
                  onLookupSuccess={handleAresLookupSuccess('vendor')}
                />
              )
            }
          />
        ),
      [control, formInputSharedProps, readonly, slots?.vendorSlot, getValues, handleAresLookupSuccess],
    );

    const currencyProps = useMemo(() => {
      const currency = currencies.find((curr) => curr.value.toLowerCase() === selectedCurrency?.toLowerCase());
      return {
        position: currency?.position ?? 'suffix',
        unicode: currency?.unicode ?? '',
      };
    }, [selectedCurrency]);

    return (
      <Stack
        {...stackProps}
        id={formId}
        component={'form'}
        ref={ref}
        autoComplete={'off'}
        spacing={{
          xs: 2,
          md: 6,
        }}
        sx={{ overflow: 'hidden' }}
      >
        {/*CUSTOMER AND Vendor SECTION*/}
        <Grid
          container
          spacing={{
            xs: 2,
            md: 6,
          }}
        >
          <Grid
            xs={12}
            container
            spacing={{
              xs: 2,
              md: 6,
            }}
          >
            <Grid xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant={'h4'}>{t('invoice')}</Typography>
                {slots?.invoiceHeaderSlot}
              </Box>
            </Grid>
            <Grid xs={6}>
              <FormInput
                {...formInputSharedProps}
                readonly={readonly}
                control={control}
                name={'number'}
                label={t('invoice.number') + '*'}
              />
            </Grid>
          </Grid>
          <Grid xs={12} md={6}>
            {/* Vendor */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant={'h4'}>{t('vendor')}</Typography>
                {slots?.vendorHeaderSlot}
              </Box>
              {VendorSlot}
            </Box>
          </Grid>
          <Grid xs={12} md={6} pb={0}>
            {/* CUSTOMER */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant={'h4'}>{t('customer')}</Typography>
                {slots?.customerHeaderSlot}
              </Box>
              {CustomerSlot}
            </Box>
          </Grid>
          <Grid xs={12} container>
            <Grid xs={12} paddingBottom={0}>
              <Typography variant={'h4'}>{t('paymentInfo')}</Typography>
            </Grid>
            <Grid
              xs={12}
              container
              spacing={{
                xs: 2,
                md: 8,
              }}
            >
              <Grid xs={12} md={6} container>
                <Grid xs={12}>
                  <ConnectedFormFieldGroup
                    readonly={readonly}
                    label={'\xa0'}
                    formNames={[]}
                    sx={{
                      border: 0,
                      px: 0,
                    }}
                  >
                    <Grid xs={12} container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormDatePicker
                          readonly={readonly}
                          textFieldProps={formInputSharedProps}
                          control={control}
                          name={'issueDate'}
                          label={t('issueDate') + '*'}
                        />
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormDatePicker
                          readonly={readonly}
                          textFieldProps={formInputSharedProps}
                          control={control}
                          name={'dueDate'}
                          label={t('dueDate') + '*'}
                        />
                      </Grid>
                      <Grid xs={12} container spacing={2}>
                        {readonly && !taxPayer ? null : (
                          <Grid xs={12} md={6}>
                            <FormDatePicker
                              readonly={readonly}
                              textFieldProps={formInputSharedProps}
                              control={control}
                              name={'taxPoint'}
                              label={t('taxPoint') + (taxPayer ? ' *' : '')}
                              disabled={!taxPayer}
                            />
                          </Grid>
                        )}
                        <Grid xs={12} md={6}>
                          <FormInput
                            {...formInputSharedProps}
                            control={control}
                            name={'variableSymbol'}
                            label={t('variableSymbol') + '*'}
                            readonly={readonly}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </ConnectedFormFieldGroup>
                </Grid>
              </Grid>
              <Grid xs={12} md={6} container>
                <Grid xs={12}>
                  <ConnectedFormFieldGroup
                    readonly={readonly}
                    required={true}
                    label={'Bankovní informace'}
                    helperText={t('IbanOrBankAccountAreRequired') as string}
                    formNames={['bankAccount', 'bankNumber', 'iban', 'swift']}
                  >
                    <Grid xs={12} container spacing={2}>
                      <Grid xs={8}>
                        <FormInput
                          {...formInputSharedProps}
                          disableRequiredErrorIndication={true}
                          readonly={readonly}
                          control={control}
                          name={'bankAccount'}
                          label={t('bankAccount')}
                          InputProps={{
                            ...formInputSharedProps,
                            endAdornment: <InputAdornment position={'end'}>/</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid xs={4}>
                        <FormInput
                          {...formInputSharedProps}
                          disableRequiredErrorIndication={true}
                          readonly={readonly}
                          control={control}
                          name={'bankNumber'}
                          label={t('bankNumber')}
                        />
                      </Grid>
                      <Grid xs={12} md={8}>
                        <FormInput
                          {...formInputSharedProps}
                          disableRequiredErrorIndication={true}
                          readonly={readonly}
                          control={control}
                          name={'iban'}
                          label={t('iban')}
                        />
                      </Grid>
                      <Grid xs={12} md={4}>
                        <FormInput
                          {...formInputSharedProps}
                          disableRequiredErrorIndication={true}
                          readonly={readonly}
                          control={control}
                          name={'swift'}
                          label={t('swift')}
                        />
                      </Grid>
                    </Grid>
                  </ConnectedFormFieldGroup>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid xs={12} container>
            <Grid xs={12} paddingBottom={0}>
              <Typography variant={'h4'}>{'Položky faktury'}</Typography>
            </Grid>
            <Grid xs={12}>
              <InvoiceItemsFormPart
                readonly={readonly}
                control={control}
                setValue={setValue}
                inputProps={formInputSharedProps}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              height: '1px',
              borderTopWidth: 1,
              borderTopStyle: 'dashed',
              borderTopColor: 'divider',
              position: 'relative',
              width: '100%',
              mt: { xs: 4, md: 1 },
              mb: { xs: 2, md: 0 },
            }}
          ></Box>
          <Grid xs={12} container>
            <Grid xs={12} container spacing={2}>
              {readonly && !taxPayer ? null : (
                <Grid xs={8}>
                  <AutoFilledAmountWithoutVatInput
                    setValue={setValue}
                    baseName={'items'}
                    readonly={true}
                    disabled={!taxPayer}
                    variant={'standard'}
                    control={control}
                    name={'amountWithoutVat'}
                    label={t('invoice.amountWithoutVat') + (taxPayer ? ' *' : '')}
                    helperText={''}
                    numericInputProps={{
                      [currencyProps.position]: ` ${currencyProps?.unicode} `,
                    }}
                    InputProps={formInputSharedProps}
                  />
                </Grid>
              )}
              {readonly && !taxPayer ? null : <Grid xs={4}>{/* Empty grid for alignment */}</Grid>}
              {readonly && !taxPayer ? null : (
                <Grid xs={8}>
                  <AutoFilledVatSumInput
                    setValue={setValue}
                    baseName={'items'}
                    readonly={true}
                    disabled={!taxPayer}
                    variant={'standard'}
                    control={control}
                    name={'amountVat'}
                    label={t('invoice.amountVat') + (taxPayer ? ' *' : '')}
                    helperText={''}
                    numericInputProps={{
                      [currencyProps.position]: ` ${currencyProps?.unicode} `,
                    }}
                    InputProps={formInputSharedProps}
                  />
                </Grid>
              )}
              {readonly && !taxPayer ? null : <Grid xs={4}>{/* Empty grid for alignment */}</Grid>}

              <Grid xs={8}>
                <AutoFilledPriceSumInput
                  setValue={setValue}
                  baseName={'items'}
                  readonly={true}
                  variant={'standard'}
                  control={control}
                  name={'amount'}
                  label={t('invoice.amount') + '*'}
                  helperText={readonly ? '' : t('helperText.amount')}
                  numericInputProps={{
                    [currencyProps.position]: ` ${currencyProps?.unicode} `,
                  }}
                  InputProps={formInputSharedProps}
                />
              </Grid>
              <Grid xs={4}>
                <FormSelect
                  selectFieldProps={formInputSharedProps}
                  readonly={readonly}
                  variant={'standard'}
                  control={control}
                  name={'currency'}
                  label={t('currency') + '*'}
                  options={currencies}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Stack>
    );
  },
);

type AutoFilledPriceSumInputProps = {
  control: Control<InvoiceManagementInvoiceFormType>;
  setValue: UseFormSetValue<InvoiceManagementInvoiceFormType>;
  baseName: FieldPath<InvoiceManagementInvoiceFormType>;
} & FormNumericInputProps;

const AutoFilledPriceSumInput = ({ control, setValue, baseName, ...rest }: AutoFilledPriceSumInputProps) => {
  const results = useWatch({
    control,
    name: baseName,
    defaultValue: [],
  });

  const sum = useMemo(() => {
    if (!Array.isArray(results) || results.length === 0) {
      return undefined;
    }

    return results.reduce((acc, item) => acc + Number(item.priceSum ?? 0), 0);
  }, [results]);

  useEffect(() => {
    if (sum !== undefined) {
      setValue(rest.name as FieldPath<InvoiceManagementInvoiceFormType>, sum);
    }
  }, [sum, setValue, rest.name]);

  return <FormNumericInput control={control} {...rest} />;
};

type AutoFilledVatSumInputProps = {
  control: Control<InvoiceManagementInvoiceFormType>;
  setValue: UseFormSetValue<InvoiceManagementInvoiceFormType>;
  baseName: FieldPath<InvoiceManagementInvoiceFormType>;
} & FormNumericInputProps;

const AutoFilledVatSumInput = ({ control, setValue, baseName, ...rest }: AutoFilledVatSumInputProps) => {
  const results = useWatch({
    control,
    name: baseName,
  });

  const taxPayer = useWatch({
    control,
    name: 'taxPayer',
  });

  // Calculate the total VAT amount
  const totalVat = useMemo(() => {
    if (!Array.isArray(results) || !taxPayer) {
      return 0;
    }

    return results.reduce((total, item) => {
      const priceWithoutVat = Number(item.priceWithoutVat ?? 0);
      const priceWithVat = Number(item.priceSum ?? 0);
      if (!isNaN(priceWithoutVat) && !isNaN(priceWithVat)) {
        // The VAT amount is the difference between price with VAT and price without VAT
        return total + (priceWithVat - priceWithoutVat);
      }
      return total;
    }, 0);
  }, [results, taxPayer]);

  useEffect(() => {
    if (taxPayer) {
      setValue(rest.name as FieldPath<InvoiceManagementInvoiceFormType>, totalVat.toFixed(2));
    } else {
      // Explicitly set to null when not a tax payer
      setValue(rest.name as FieldPath<InvoiceManagementInvoiceFormType>, null);
    }
  }, [totalVat, setValue, rest.name, taxPayer]);

  return <FormNumericInput control={control} {...rest} />;
};

type AutoFilledAmountWithoutVatInputProps = {
  control: Control<InvoiceManagementInvoiceFormType>;
  setValue: UseFormSetValue<InvoiceManagementInvoiceFormType>;
  baseName: FieldPath<InvoiceManagementInvoiceFormType>;
} & FormNumericInputProps;

const AutoFilledAmountWithoutVatInput = ({
  control,
  setValue,
  baseName,
  ...rest
}: AutoFilledAmountWithoutVatInputProps) => {
  const results = useWatch({
    control,
    name: baseName,
  });

  const taxPayer = useWatch({
    control,
    name: 'taxPayer',
  });

  // Calculate the total amount without VAT
  const totalWithoutVat = useMemo(() => {
    if (!taxPayer) {
      return;
    }

    if (!Array.isArray(results)) {
      return 0;
    }

    return results.reduce((total, item) => {
      const priceWithoutVat = Number(item.priceWithoutVat ?? 0);
      if (!isNaN(priceWithoutVat)) {
        return total + priceWithoutVat;
      }

      return total;
    }, 0);
  }, [results, taxPayer]);

  useEffect(() => {
    if (totalWithoutVat && taxPayer) {
      setValue(rest.name as FieldPath<InvoiceManagementInvoiceFormType>, totalWithoutVat.toFixed(2));
    } else if (!taxPayer) {
      // Explicitly set to null when not a tax payer
      setValue(rest.name as FieldPath<InvoiceManagementInvoiceFormType>, null);
    }
  }, [totalWithoutVat, setValue, rest.name, taxPayer]);

  return <FormNumericInput control={control} {...rest} />;
};

InvoiceManagementInvoiceForm.displayName = 'InvoiceManagementInvoiceForm';
